import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  AlertTriangle,
  ArrowLeft,
  ArrowRight,
  Briefcase,
  CalendarDays,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  ChevronsUpDown,
  Copy,
  ExternalLink,
  Globe,
  ImageOff,
  Layers,
  LoaderCircle,
  MousePointerClick,
  Navigation,
  Pencil,
  Save,
  Search,
  ShieldCheck,
  Trash2,
  Variable,
  WandSparkles,
  X,
} from 'lucide-react';
import { API_BASE_URL, API_ENDPOINTS } from '../../constants/api';
import { apiFetch } from '../../utils/apiClient';
import {
  CONSUMABLE_STEP_ACTIONS,
  formatCountLabel,
  journeyVariableNamesById,
  normalizeAction,
  qualityForStep,
  stepSelectionId,
  stepValueBinding,
  verificationSummary,
} from './capturedFlowHelpers';

const ACTION_ICONS = {
  navigate: Navigation,
  click: MousePointerClick,
  fill: Pencil,
  select: ChevronsUpDown,
  check: CheckCircle2,
  uncheck: CheckCircle2,
  assert: ShieldCheck,
  capture_value: Variable,
  tab_switch: Layers,
};

// A capture can still contain one unusually large page, so render that page in slices.
const GROUP_ROW_LIMIT = 100;

const titleCase = (value) => String(value || '')
  .replace(/[-_]+/g, ' ')
  .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
  .replace(/\s+/g, ' ')
  .trim()
  .replace(/\b\w/g, (letter) => letter.toUpperCase());

/**
 * The friendliest name we can prove for the page a step ran on: the title recorded with
 * the screenshot, then the route it was captured at, then the host.
 */
const pageLabelForStep = (step) => {
  const pageName = String(step?.page_name || '').trim();
  if (pageName) return pageName.slice(0, 80);
  const title = String(step?.screenshot?.title || '').trim();
  if (title) return title.slice(0, 80);
  const path = String(step?.url_path || '').trim();
  if (path && path !== '/') {
    const segment = path.split('/').filter(Boolean).pop() || '';
    const cleaned = segment.replace(/\.[a-z0-9]+$/i, '');
    if (cleaned) return titleCase(cleaned).slice(0, 80);
  }
  const url = String(step?.url || '').trim();
  if (url) {
    try {
      return new URL(url).hostname.replace(/^www\./, '');
    } catch {
      return url.slice(0, 60);
    }
  }
  return 'Page';
};

const URL_BINDING_TOKEN = '__url_binding_value__';

const urlForValidation = (value) => String(value || '').replace(
  /\{\{\s*[A-Za-z_][A-Za-z0-9_]*\s*\}\}/g,
  URL_BINDING_TOKEN
);

/**
 * Split the flow the way a tester walked it: each navigation opens a page, and the steps
 * that follow belong to it. The navigation becomes the group heading instead of a row,
 * which is what removes most of the repetition from a recorded journey.
 */
export const buildPageGroups = (steps = []) => {
  const groups = [];
  let current = null;

  steps.forEach((step, index) => {
    const entry = { step, index, id: stepSelectionId(step, index) };
    if (String(step?.action || '').toLowerCase() === 'navigate' || !current) {
      const isNavigation = String(step?.action || '').toLowerCase() === 'navigate';
      current = {
        key: `page-${groups.length + 1}`,
        pageNumber: groups.length + 1,
        label: pageLabelForStep(step),
        url: String(step?.url || ''),
        navigation: isNavigation ? entry : null,
        entries: isNavigation ? [] : [entry],
      };
      groups.push(current);
      return;
    }
    current.entries.push(entry);
  });

  return groups.map((group) => ({
    ...group,
    allEntries: group.navigation ? [group.navigation, ...group.entries] : group.entries,
  }));
};

const ActionCell = ({ action }) => {
  const Icon = ACTION_ICONS[String(action || '').toLowerCase()] || MousePointerClick;
  return (
    <span className="flex items-center gap-2 truncate text-[13px] font-semibold text-slate-900">
      <Icon className="h-3.5 w-3.5 flex-shrink-0 text-slate-400" aria-hidden="true" />
      <span className="truncate">{normalizeAction(action)}</span>
    </span>
  );
};

/* ------------------------------------------------------------------ summary --- */

const MetaChip = ({ icon: Icon, children, title, href }) => {
  const inner = (
    <>
      {Icon && <Icon className="h-3.5 w-3.5 flex-shrink-0 text-slate-400" aria-hidden="true" />}
      <span className="truncate">{children}</span>
    </>
  );
  const base = 'inline-flex max-w-full items-center gap-1.5 truncate rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-xs font-medium text-slate-600';
  if (href) {
    return (
      <a href={href} target="_blank" rel="noreferrer" title={title || String(children || '')} className={`${base} max-w-[26rem] transition-colors hover:border-slate-300 hover:bg-white hover:text-slate-900`}>
        {inner}
      </a>
    );
  }
  return (
    <span title={title} className={base}>
      {inner}
    </span>
  );
};

/**
 * Identity of the capture, where it came from, and the editable test context.
 */
export const CapturedFlowSummary = ({
  featureName,
  targetUrl,
  updatedLabel,
  applicationName,
  featureAreaName,
  actions = null,
}) => {
  const targetHref = /^https?:\/\//i.test(String(targetUrl || '')) ? String(targetUrl) : undefined;
  return (
  <section className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm sm:p-5">
    <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
      <div className="flex min-w-0 items-start gap-3.5">
        <span className="workspace-context-icon inline-flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl text-white">
          <Navigation className="h-5 w-5" aria-hidden="true" />
        </span>
        <div className="min-w-0">
          <h3 className="truncate text-lg font-bold text-slate-900" title={featureName}>
            {featureName}
          </h3>
          <div className="mt-2 flex flex-wrap items-center gap-1.5">
            <MetaChip icon={ExternalLink} title={targetUrl} href={targetHref}>{targetUrl}</MetaChip>
            {updatedLabel && <MetaChip icon={CalendarDays} title="Last captured">{updatedLabel}</MetaChip>}
            {applicationName && <MetaChip icon={Briefcase} title="Application">{applicationName}</MetaChip>}
            {featureAreaName && <MetaChip icon={Globe} title="Feature area">{featureAreaName}</MetaChip>}
          </div>
        </div>
      </div>
      {actions}
    </div>
  </section>
  );
};

/* --------------------------------------------------------------------- tabs --- */

export const CapturedFlowTabs = ({ tabs = [], activeId, onChange, ariaLabel = 'Captured flow sections' }) => {
  const buttonRefs = useRef([]);
  if (tabs.length < 2) return null;

  const handleKeyDown = (event, index) => {
    if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;
    event.preventDefault();
    const offset = event.key === 'ArrowRight' ? 1 : -1;
    const nextIndex = (index + offset + tabs.length) % tabs.length;
    onChange?.(tabs[nextIndex].id);
    buttonRefs.current[nextIndex]?.focus();
  };

  return (
    <div
      role="tablist"
      aria-label={ariaLabel}
      className="inline-flex max-w-full flex-wrap items-center gap-1 rounded-xl border border-slate-200 bg-slate-100/80 p-1"
    >
      {tabs.map((tab, index) => {
        const isActive = tab.id === activeId;
        return (
          <button
            key={tab.id}
            ref={(node) => { buttonRefs.current[index] = node; }}
            type="button"
            role="tab"
            id={`captured-flow-tab-${tab.id}`}
            aria-selected={isActive}
            aria-controls={`captured-flow-panel-${tab.id}`}
            tabIndex={isActive ? 0 : -1}
            onClick={() => onChange?.(tab.id)}
            onKeyDown={(event) => handleKeyDown(event, index)}
            className={`inline-flex h-9 items-center gap-2 rounded-lg px-3.5 text-sm font-semibold transition-all ${
              isActive
                ? 'bg-white text-slate-900 shadow-sm ring-1 ring-slate-200/80'
                : 'text-slate-500 hover:bg-white/60 hover:text-slate-900'
            }`}
          >
            <span>{tab.label}</span>
            {tab.count !== undefined && tab.count !== null && (
              <span
                className={`rounded-full px-1.5 py-0.5 text-[11px] font-bold ${
                  tab.tone === 'danger'
                    ? 'bg-rose-100 text-rose-700'
                    : isActive
                      ? 'workspace-accent-chip border'
                      : 'bg-slate-200/70 text-slate-600'
                }`}
              >
                {tab.count}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
};

/* ----------------------------------------------------------------- overview --- */

const CAPTURE_TAB_ROLE_LABELS = {
  primary: 'Flow tab',
  reference: 'Read only',
  interactive: 'Recorded tab',
};

/**
 * Every site the capture touched, and why.
 *
 * A capture that reached into a second tab recorded something outside the application
 * under test, so the origins are shown before generation rather than buried in the steps.
 */
export const CaptureTabsPanel = ({ tabs = [] }) => {
  if (tabs.length < 2) return null;
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
      <h3 className="text-sm font-bold text-slate-900">Tabs used by this flow</h3>
      <p className="mt-1 text-xs leading-5 text-slate-500">
        This journey moved between tabs. The generated test opens each page and brings the
        right one to the front before it acts.
      </p>
      <div className="mt-3 grid gap-2">
        {tabs.map((tab) => (
          <div
            key={tab.tab_key}
            className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2"
          >
            <div className="min-w-0">
              <p className="truncate text-sm font-semibold text-slate-900">
                {tab.label || tab.origin || tab.tab_key}
              </p>
              <p className="truncate text-xs text-slate-500">
                {[
                  tab.tab_key,
                  tab.origin,
                  tab.opened_by_tab_key ? `opened from ${tab.opened_by_tab_key}` : '',
                  `${tab.step_count || 0} step(s)`,
                ].filter(Boolean).join(' · ')}
              </p>
            </div>
            <span
              className={`rounded-full px-2 py-1 text-xs font-semibold ${
                tab.role === 'reference'
                  ? 'bg-indigo-50 text-indigo-700'
                  : 'bg-emerald-50 text-emerald-700'
              }`}
            >
              {CAPTURE_TAB_ROLE_LABELS[tab.role] || tab.role}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

/**
 * What the flow is about and where it went — the reading view of the capture, kept apart
 * from the step-by-step detail so neither one crowds the other.
 */
export const CapturedFlowOverview = ({
  notes = '',
  onNotesChange,
  canEditNotes = true,
  isEditingNotes = false,
  onToggleNotesEditing,
  pageGroups = [],
  captureTabs = [],
  onSelectPage,
}) => (
  <div className="space-y-4">
    <div className="grid gap-4 lg:grid-cols-2">
      <section className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
        <h3 className="text-sm font-bold text-slate-900">Flow summary</h3>
        {isEditingNotes ? (
          <>
            <label className="mt-2 block">
              <span className="sr-only">Flow rules and additional context</span>
              <textarea
                value={notes}
                onChange={(event) => onNotesChange?.(event.target.value)}
                className="text-input min-h-[140px] resize-y"
                placeholder="Example: add rules like required validations, success end state, roles, data constraints, or assertions to include in test cases and scripts"
              />
            </label>
            <button
              type="button"
              onClick={() => onToggleNotesEditing?.(false)}
              className="secondary-btn mt-3 px-3 py-1.5 text-xs"
            >
              Done
            </button>
          </>
        ) : (
          <>
            <p className="mt-2 whitespace-pre-line text-xs leading-6 text-slate-600">
              {notes.trim() || 'No flow rules added yet. Add validations, end state, roles, or data constraints so generated cases and scripts follow them.'}
            </p>
            {canEditNotes && (
              <button
                type="button"
                onClick={() => onToggleNotesEditing?.(true)}
                className="secondary-btn mt-3 px-3 py-1.5 text-xs"
              >
                <Pencil className="h-3.5 w-3.5" />
                Edit context
              </button>
            )}
          </>
        )}
      </section>

      <section className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
        <h3 className="text-sm font-bold text-slate-900">Journey preview</h3>
        {pageGroups.length ? (
          <ol className="mt-2 space-y-1">
            {pageGroups.map((group) => (
              <li key={group.key}>
                <button
                  type="button"
                  onClick={() => onSelectPage?.(group.key)}
                  className="flex w-full items-center gap-3 rounded-xl px-2 py-2 text-left transition-colors hover:bg-slate-50"
                >
                  <span className="workspace-accent-chip inline-flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full border text-[11px] font-bold">
                    {group.pageNumber}
                  </span>
                  <span className="min-w-0 flex-1 truncate text-sm font-semibold text-slate-800" title={group.url}>
                    {group.label}
                  </span>
                  <span className="flex-shrink-0 text-xs text-slate-500">
                    {formatCountLabel(group.allEntries.length, 'step')}
                  </span>
                </button>
              </li>
            ))}
          </ol>
        ) : (
          <p className="mt-2 text-xs leading-6 text-slate-500">No pages recorded yet.</p>
        )}
      </section>
    </div>

    <CaptureTabsPanel tabs={captureTabs} />
  </div>
);

/* -------------------------------------------------------------- step values --- */

export const StepValueBinding = ({ binding, onUse, canEdit = false, isSaving = false, variables = [] }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [openUpward, setOpenUpward] = useState(false);
  const pickerRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return undefined;
    const closeOnOutsideClick = (event) => {
      if (!pickerRef.current?.contains(event.target)) setIsOpen(false);
    };
    const closeOnEscape = (event) => {
      if (event.key === 'Escape') setIsOpen(false);
    };
    document.addEventListener('mousedown', closeOnOutsideClick);
    document.addEventListener('keydown', closeOnEscape);
    return () => {
      document.removeEventListener('mousedown', closeOnOutsideClick);
      document.removeEventListener('keydown', closeOnEscape);
    };
  }, [isOpen]);

  if (!binding && (!canEdit || !variables.length)) {
    return <span className="text-xs text-slate-400">—</span>;
  }
  if (binding?.kind === 'produces') {
    return (
      <span className="inline-flex max-w-full items-center gap-1 truncate rounded-full bg-teal-50 px-2 py-1 text-xs font-semibold text-teal-700">
        <ArrowRight className="h-3 w-3 shrink-0" />
        <span className="truncate font-mono">{binding.name}</span>
      </span>
    );
  }
  if (binding?.kind === 'consumes' && (!canEdit || !variables.length)) {
    return (
      <span
        className={`inline-flex max-w-full items-center gap-1 truncate rounded-full px-2 py-1 text-xs font-semibold ${
          binding.missing ? 'bg-rose-50 text-rose-700' : 'bg-indigo-50 text-indigo-700'
        }`}
      >
        <ArrowLeft className="h-3 w-3 shrink-0" />
        <span className="truncate font-mono">{binding.missing ? 'missing value' : binding.name}</span>
      </span>
    );
  }
  if (binding?.kind === 'suggested' && (!canEdit || !variables.length)) {
    return (
      <span
        className="inline-flex max-w-full items-center gap-1 truncate rounded-full border border-amber-200 bg-amber-50 px-2 py-1 text-xs font-semibold text-amber-800"
        title={binding.reason || `Suggested mapping to ${binding.name}`}
      >
        <ArrowLeft className="h-3 w-3 shrink-0" />
        <span className="truncate font-mono">{binding.name}</span>
        <span className="font-sans text-[10px] uppercase tracking-wide text-amber-600">suggested</span>
      </span>
    );
  }
  if (!canEdit || !variables.length) {
    return <span className="text-xs text-slate-400">—</span>;
  }
  const selectedVariableId = binding?.kind === 'consumes' ? String(binding.variableId || '') : '';
  const selectedVariable = variables.find((variable) => String(variable.id) === selectedVariableId);
  const suggestedVariableId = binding?.kind === 'suggested' ? String(binding.variableId || '') : '';
  const triggerLabel = selectedVariable?.name || (binding?.kind === 'consumes' ? binding.name : '') || 'Use test data';
  const choose = (variableId) => {
    if (isSaving) return;
    setIsOpen(false);
    if (String(variableId) !== selectedVariableId) onUse?.(String(variableId));
  };
  const togglePicker = () => {
    if (isSaving) return;
    if (isOpen) {
      setIsOpen(false);
      return;
    }
    const bounds = pickerRef.current?.getBoundingClientRect();
    const panelBounds = pickerRef.current?.closest('section')?.getBoundingClientRect();
    const visiblePanelBottom = Math.min(window.innerHeight, panelBounds?.bottom ?? window.innerHeight);
    const visiblePanelTop = Math.max(0, panelBounds?.top ?? 0);
    const availableBelow = bounds ? visiblePanelBottom - bounds.bottom : window.innerHeight;
    const availableAbove = bounds ? bounds.top - visiblePanelTop : 0;
    const estimatedMenuHeight = Math.min(230, 48 + ((variables.length + 1) * 38));
    setOpenUpward(Boolean(
      bounds
      && availableBelow < estimatedMenuHeight
      && availableAbove > availableBelow
    ));
    setIsOpen(true);
  };
  return (
    <div
      ref={pickerRef}
      className="relative grid min-w-[300px] grid-cols-[minmax(0,1fr)_112px] items-center gap-1.5"
      onClick={(event) => event.stopPropagation()}
    >
      <button
        type="button"
        aria-label="Runtime value mapping"
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        disabled={isSaving}
        onClick={togglePicker}
        className="group flex min-w-0 flex-1 items-center rounded-lg border border-slate-200 bg-white px-2 py-1.5 text-left shadow-sm transition-colors hover:border-teal-300 focus:outline-none focus:ring-2 focus:ring-teal-500/20 disabled:cursor-wait disabled:opacity-70"
      >
        <ArrowLeft className="h-3.5 w-3.5 shrink-0 text-teal-600" />
        <span className="ml-1.5 min-w-0 flex-1 truncate text-xs font-semibold text-slate-700">
          {triggerLabel}
        </span>
        <ChevronsUpDown className="ml-2 h-3.5 w-3.5 shrink-0 text-slate-400 transition-colors group-hover:text-teal-600" />
      </button>

      {suggestedVariableId && (
        <button
          type="button"
          disabled={isSaving}
          onClick={() => choose(suggestedVariableId)}
          className="inline-flex w-full min-w-0 items-center justify-center gap-1.5 rounded-lg border border-amber-300 bg-amber-50 px-2 py-1.5 text-[11px] font-bold text-amber-900 shadow-sm transition-colors hover:bg-amber-100 focus:outline-none focus:ring-2 focus:ring-amber-400/30 disabled:cursor-wait disabled:opacity-60"
          title={binding.reason || `Suggested mapping to ${binding.name}`}
          aria-label={`Use suggested value ${binding.name}`}
        >
          <WandSparkles className="h-3.5 w-3.5 shrink-0" />
          <span className="truncate font-mono">{binding.name}</span>
        </button>
      )}
      {!suggestedVariableId && <span aria-hidden="true" />}

      {isOpen && (
        <div
          role="listbox"
          aria-label="Choose runtime value"
          className={`absolute right-0 z-50 w-[238px] overflow-hidden rounded-xl border border-slate-200 bg-white p-1 shadow-[0_14px_32px_rgba(15,23,42,0.16)] ${
            openUpward ? 'bottom-full mb-1' : 'top-full mt-1'
          }`}
        >
          <div className="border-b border-slate-100 px-2 py-1.5">
            <p className="text-[10px] font-bold uppercase tracking-[0.12em] text-slate-400">Map input to</p>
          </div>
          <div className="max-h-44 overflow-y-auto py-0.5">
            <button
              type="button"
              role="option"
              aria-selected={!selectedVariableId}
              onClick={() => choose('')}
              className="flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left hover:bg-slate-50"
            >
              <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-slate-100 text-slate-500">—</span>
              <span className="min-w-0 flex-1">
                <span className="block text-xs font-semibold text-slate-700">Use test data</span>
              </span>
              {!selectedVariableId && <CheckCircle2 className="h-4 w-4 shrink-0 text-teal-600" />}
            </button>
            {variables.map((variable) => {
              const selected = String(variable.id) === selectedVariableId;
              const context = variable.optionLabel && variable.optionLabel !== variable.name
                ? String(variable.optionLabel).replace(`${variable.name} - `, '')
                : variable.description || 'Captured earlier in this flow';
              return (
                <button
                  key={variable.id}
                  type="button"
                  role="option"
                  aria-selected={selected}
                  onClick={() => choose(variable.id)}
                  className={`flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left transition-colors ${selected ? 'bg-teal-50' : 'hover:bg-slate-50'}`}
                >
                  <span className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-md ${selected ? 'bg-teal-100 text-teal-700' : 'bg-indigo-50 text-indigo-600'}`}>
                    <Variable className="h-3.5 w-3.5" />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate font-mono text-xs font-semibold text-slate-800">{variable.name}</span>
                    <span className="block truncate text-[11px] text-slate-400">{context}</span>
                  </span>
                  {selected && <CheckCircle2 className="h-4 w-4 shrink-0 text-teal-600" />}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

/* --------------------------------------------------------------- steps list --- */

const STEP_GRID = 'grid-cols-[32px_38px_minmax(0,1fr)_72px] sm:grid-cols-[36px_48px_120px_minmax(0,1fr)_minmax(320px,0.8fr)_84px]';

const StepRow = ({
  entry,
  isSelected,
  isActive,
  canSelect,
  canRemoveSteps,
  onToggleStep,
  onOpenStep,
  binding,
  bindingEditable,
  bindingVariables,
  isSavingBinding = false,
  onUseJourneyVariable,
  tabLabel,
}) => {
  const { step, index, id: stepId } = entry;
  const quality = qualityForStep(step);
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => onOpenStep(stepId)}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onOpenStep(stepId);
        }
      }}
      className={`grid ${STEP_GRID} cursor-pointer items-center px-3 py-2 text-sm transition-colors ${
        isActive ? 'workspace-accent-soft-panel' : isSelected ? 'bg-teal-50/45' : 'hover:bg-slate-50'
      }`}
    >
      <span className="flex items-center" onClick={(event) => event.stopPropagation()}>
        {canSelect && (
          <input
            type="checkbox"
            checked={isSelected}
            onChange={() => onToggleStep(stepId)}
            disabled={!canRemoveSteps}
            className="h-4 w-4 rounded border-slate-300 text-teal-600 focus:ring-teal-500 disabled:cursor-not-allowed disabled:opacity-60"
            aria-label={`Select captured step ${index + 1}`}
          />
        )}
      </span>
      <span className="text-xs font-semibold text-slate-500">{index + 1}</span>
      <span className="hidden sm:block"><ActionCell action={step.action} /></span>
      <span className="min-w-0 truncate pr-3 text-[13px] text-slate-700" title={step.target}>
        <span className="font-semibold text-slate-500 sm:hidden">{`${normalizeAction(step.action)} · `}</span>
        {step.target || step.label || 'Target'}
        {verificationSummary(step) && (
          <span className="ml-2 rounded-full bg-orange-50 px-2 py-0.5 text-[11px] font-semibold text-orange-700">
            {verificationSummary(step)}
          </span>
        )}
        {tabLabel && (
          <span className="ml-2 rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-600">
            {tabLabel}
          </span>
        )}
      </span>
      <span className="hidden min-w-0 pr-3 sm:block">
        <StepValueBinding
          binding={binding}
          canEdit={bindingEditable}
          isSaving={isSavingBinding}
          variables={bindingVariables}
          onUse={(variableId) => onUseJourneyVariable?.(stepId, variableId)}
        />
      </span>
      <span className={`rounded-full px-2 py-1 text-center text-[11px] font-semibold ${quality.className}`}>
        {quality.label}
      </span>
    </div>
  );
};

const CapturedStepsPanelBase = ({
  steps,
  capturedCount,
  selectedStepIds = [],
  onToggleStep,
  onToggleAllSteps,
  onRemoveSelectedSteps,
  isRemovingSelectedSteps = false,
  canRemoveSteps = true,
  journeyVariables = [],
  onUseJourneyVariable,
  onApplySuggestedMappings,
  isSavingBinding = false,
  captureTabs = [],
  activeStepId = '',
  onOpenStep,
  focusPageKey = '',
}) => {
  const [query, setQuery] = useState('');
  const [actionFilter, setActionFilter] = useState('all');
  const [qualityFilter, setQualityFilter] = useState('all');
  const [pageFilter, setPageFilter] = useState('');
  const [expandedGroupKeys, setExpandedGroupKeys] = useState(() => new Set());

  const selectedSet = useMemo(() => new Set(selectedStepIds), [selectedStepIds]);
  // Which tab a step ran in only means anything once the journey used more than one.
  const showStepTabs = captureTabs.length > 1;
  const tabLabelsByKey = useMemo(
    () => new Map(captureTabs.map((tab) => [String(tab.tab_key || ''), String(tab.label || tab.tab_key || '')])),
    [captureTabs]
  );
  const variableNames = useMemo(() => journeyVariableNamesById(journeyVariables), [journeyVariables]);
  const canEditBindings = Boolean(onUseJourneyVariable);
  const canSelect = Boolean(onToggleStep && onToggleAllSteps && onRemoveSelectedSteps && steps.length);

  const groups = useMemo(() => buildPageGroups(steps), [steps]);

  useEffect(() => {
    setPageFilter((current) => (
      groups.find((group) => group.key === focusPageKey)?.key
      || (groups.some((group) => group.key === current) ? current : groups[0]?.key || '')
    ));
    setExpandedGroupKeys(new Set());
  }, [focusPageKey, groups]);

  const variableIndexById = useMemo(() => {
    const orderByStepId = new Map(steps.map((step, index) => [stepSelectionId(step, index), index]));
    return new Map(journeyVariables.map((variable) => [
      String(variable.id),
      orderByStepId.get(String(variable?.producer?.step_id || '')),
    ]));
  }, [journeyVariables, steps]);

  const tabKeyByStepId = useMemo(
    () => new Map(steps.map((step, index) => [stepSelectionId(step, index), String(step?.tab_key || '')])),
    [steps]
  );

  // Only values captured earlier can be offered: a test cannot use a value it has
  // not read yet.
  const variablesAvailableAt = useCallback((index) => journeyVariables
    .filter((variable) => {
      const producerIndex = variableIndexById.get(String(variable.id));
      return producerIndex === undefined || producerIndex < index;
    })
    .map((variable) => {
      if (!showStepTabs) return variable;
      const variableTabKey = String(
        variable?.producer?.tab_key
        || variable?.source?.tab_key
        || tabKeyByStepId.get(String(variable?.producer?.step_id || ''))
        || ''
      );
      const tabLabel = variableTabKey ? tabLabelsByKey.get(variableTabKey) || variableTabKey : '';
      return tabLabel ? { ...variable, optionLabel: `${variable.name} - ${tabLabel}` } : variable;
    }), [journeyVariables, showStepTabs, tabKeyByStepId, tabLabelsByKey, variableIndexById]);

  const actionOptions = useMemo(() => {
    const seen = new Map();
    steps.forEach((step) => {
      const key = String(step?.action || '').toLowerCase();
      if (!key) return;
      seen.set(key, (seen.get(key) || 0) + 1);
    });
    return Array.from(seen.entries()).map(([value, count]) => ({
      value,
      label: `${normalizeAction(value)} (${count})`,
    }));
  }, [steps]);

  const matchesFilters = useCallback((entry) => {
    const { step, index } = entry;
    if (actionFilter !== 'all' && String(step?.action || '').toLowerCase() !== actionFilter) return false;
    if (qualityFilter !== 'all' && qualityForStep(step).label !== qualityFilter) return false;
    const needle = query.trim().toLowerCase();
    if (!needle) return true;
    return [
      normalizeAction(step.action),
      step.target,
      step.label,
      step.locator,
      step.url,
      String(index + 1),
    ].filter(Boolean).join(' ').toLowerCase().includes(needle);
  }, [actionFilter, qualityFilter, query]);

  const visibleGroups = useMemo(() => groups
    .filter((group) => group.key === pageFilter)
    .map((group) => ({
      ...group,
      matchedEntries: group.entries.filter(matchesFilters),
      navigationMatches: group.navigation ? matchesFilters(group.navigation) : false,
    }))
    .filter((group) => group.matchedEntries.length > 0 || group.navigationMatches), [groups, matchesFilters, pageFilter]);

  const activePageIndex = Math.max(0, groups.findIndex((group) => group.key === pageFilter));
  const activePage = groups[activePageIndex] || null;
  const activePageSuggestions = useMemo(() => (activePage?.entries || []).flatMap((entry) => {
    if (!CONSUMABLE_STEP_ACTIONS.has(String(entry.step.action || '').toLowerCase())) return [];
    const binding = stepValueBinding(entry.step, variableNames);
    if (binding?.kind !== 'suggested') return [];
    const isAvailable = variablesAvailableAt(entry.index).some(
      (variable) => String(variable.id) === String(binding.variableId)
    );
    return isAvailable ? [{ stepId: entry.id, variableId: binding.variableId }] : [];
  }), [activePage, variableNames, variablesAvailableAt]);
  const isFiltered = Boolean(query.trim()) || actionFilter !== 'all' || qualityFilter !== 'all';
  const matchedCount = visibleGroups.reduce(
    (total, group) => total + group.matchedEntries.length + (group.navigationMatches ? 1 : 0),
    0
  );

  const clearFilters = () => {
    setQuery('');
    setActionFilter('all');
    setQualityFilter('all');
  };

  const movePage = (offset) => {
    const nextPage = groups[activePageIndex + offset];
    if (nextPage) setPageFilter(nextPage.key);
  };

  const openStep = useCallback((stepId) => onOpenStep?.(stepId), [onOpenStep]);

  if (!steps.length) {
    return (
      <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-8 text-center">
        <MousePointerClick className="mx-auto h-8 w-8 text-slate-400" />
        <h4 className="mt-3 text-sm font-bold text-slate-900">No captured steps yet</h4>
        <p className="mx-auto mt-1 max-w-xl text-sm leading-6 text-slate-500">
          Start capture, complete the feature in the new tab, then get this flow.
        </p>
      </div>
    );
  }

  return (
    <div className="grid items-start gap-3 lg:grid-cols-[220px_minmax(0,1fr)]">
      <aside className="overflow-hidden rounded-2xl border border-slate-200 bg-slate-50/70 p-3 shadow-sm" aria-label="Captured pages">
        <div className="mb-3 flex items-center justify-between gap-2">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">Pages</p>
            <p className="mt-1 text-sm font-bold text-slate-900">{formatCountLabel(groups.length, 'page')}</p>
          </div>
          <span className="workspace-accent-chip rounded-md border px-2 py-1 text-xs font-bold">
            {capturedCount} steps
          </span>
        </div>

        <label className="block lg:hidden">
          <span className="sr-only">Choose captured page</span>
          <select
            value={pageFilter}
            onChange={(event) => setPageFilter(event.target.value)}
            aria-label="Choose captured page"
            className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700"
          >
            {groups.map((group) => (
              <option key={group.key} value={group.key}>{`${group.pageNumber}. ${group.label}`}</option>
            ))}
          </select>
        </label>

        <nav className="hidden max-h-[620px] gap-1 overflow-y-auto pr-1 lg:grid" aria-label="Captured page list">
          {groups.map((group) => {
            const isCurrent = group.key === pageFilter;
            const attentionCount = group.allEntries.filter((entry) => {
              const label = qualityForStep(entry.step).label;
              return label === 'Review' || label === 'Missing';
            }).length;
            return (
              <button
                key={group.key}
                type="button"
                onClick={() => setPageFilter(group.key)}
                aria-current={isCurrent ? 'page' : undefined}
                aria-label={`Open page ${group.pageNumber}: ${group.label}`}
                className={`grid grid-cols-[28px_minmax(0,1fr)_auto] items-center gap-2 rounded-lg px-2 py-2 text-left transition-colors ${
                  isCurrent
                    ? 'workspace-accent-soft-panel border'
                    : 'border border-transparent hover:bg-white'
                }`}
              >
                <span className="workspace-accent-chip inline-flex h-7 w-7 items-center justify-center rounded-full border text-[11px] font-bold">
                  {group.pageNumber}
                </span>
                <span className="min-w-0">
                  <span className="block truncate text-xs font-bold text-slate-800">{group.label}</span>
                  <span className="mt-0.5 block text-[11px] text-slate-500">
                    {formatCountLabel(group.entries.length, 'action')}
                  </span>
                </span>
                {attentionCount > 0 && (
                  <span className="rounded-full bg-rose-100 px-1.5 py-0.5 text-[10px] font-bold text-rose-700" title={`${attentionCount} step(s) need review`}>
                    {attentionCount}
                  </span>
                )}
              </button>
            );
          })}
        </nav>
      </aside>

      <section className="min-w-0 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
        {activePage && (
          <header className="flex flex-col gap-3 border-b border-slate-200 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="min-w-0">
              <p className="text-xs font-semibold text-slate-500">{`Page ${activePage.pageNumber} of ${groups.length}`}</p>
              <button
                type="button"
                onClick={() => activePage.navigation && openStep(activePage.navigation.id)}
                disabled={!activePage.navigation}
                className="mt-0.5 block max-w-full truncate text-left text-base font-bold text-slate-900 disabled:cursor-default"
                title={activePage.url || activePage.label}
              >
                {activePage.label}
              </button>
              {activePage.url && <p className="mt-0.5 truncate text-xs text-slate-500">{activePage.url}</p>}
            </div>
            <div className="flex items-center gap-2 self-end sm:self-auto">
              {activePageSuggestions.length > 0 && onApplySuggestedMappings && (
                <button
                  type="button"
                  onClick={() => onApplySuggestedMappings(activePageSuggestions)}
                  disabled={isSavingBinding}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-xs font-bold text-amber-900 transition-colors hover:bg-amber-100 focus:outline-none focus:ring-2 focus:ring-amber-400/30 disabled:cursor-wait disabled:opacity-60"
                >
                  {isSavingBinding ? <LoaderCircle className="h-3.5 w-3.5 animate-spin" /> : <WandSparkles className="h-3.5 w-3.5" />}
                  {`Apply all suggested mappings (${activePageSuggestions.length})`}
                </button>
              )}
              <button
                type="button"
                onClick={() => movePage(-1)}
                disabled={activePageIndex === 0}
                aria-label="Previous captured page"
                className="rounded-lg border border-slate-200 p-2 text-slate-600 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              <button
                type="button"
                onClick={() => movePage(1)}
                disabled={activePageIndex >= groups.length - 1}
                aria-label="Next captured page"
                className="rounded-lg border border-slate-200 p-2 text-slate-600 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          </header>
        )}

        <div className="grid gap-2 border-b border-slate-200 p-3 sm:grid-cols-[minmax(220px,1fr)_auto_auto]">
          <label className="relative flex min-w-0 items-center">
            <Search className="pointer-events-none absolute left-3 h-4 w-4 text-slate-400" aria-hidden="true" />
            <input
              type="search"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search this page..."
              aria-label="Search captured steps"
              className="w-full rounded-xl border border-slate-200 bg-white py-2 pl-9 pr-8 text-sm text-slate-700 placeholder:text-slate-400 focus:border-slate-300 focus:outline-none"
            />
            {query && (
              <button
                type="button"
                onClick={() => setQuery('')}
                aria-label="Clear step search"
                className="absolute right-2.5 text-slate-400 hover:text-slate-700"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </label>
          <select
            value={actionFilter}
            onChange={(event) => setActionFilter(event.target.value)}
            aria-label="Filter steps by action"
            className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700"
          >
            <option value="all">All actions</option>
            {actionOptions.map((option) => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>
          <select
            value={qualityFilter}
            onChange={(event) => setQualityFilter(event.target.value)}
            aria-label="Filter steps by locator confidence"
            className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700"
          >
            <option value="all">All confidence</option>
            {['High', 'Medium', 'Review', 'Missing', 'Page', 'Tab'].map((label) => (
              <option key={label} value={label}>{label}</option>
            ))}
          </select>
        </div>

        {(isFiltered || (canSelect && selectedStepIds.length > 0)) && (
          <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-100 px-3 py-2">
            <p className="text-xs text-slate-500">
              {isFiltered ? `${formatCountLabel(matchedCount, 'step')} match on this page` : 'Selected steps'}
            </p>
            <div className="flex items-center gap-2">
              {isFiltered && (
                <button type="button" onClick={clearFilters} className="text-xs font-semibold text-slate-600 hover:text-slate-900">
                  Clear filters
                </button>
              )}
              {canSelect && selectedStepIds.length > 0 && (
                <button
                  type="button"
                  onClick={onRemoveSelectedSteps}
                  disabled={!canRemoveSteps || isRemovingSelectedSteps}
                  className="inline-flex items-center gap-2 rounded-lg border border-rose-200 bg-rose-50 px-3 py-1.5 text-xs font-semibold text-rose-700 transition-colors hover:bg-rose-100 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {isRemovingSelectedSteps ? <LoaderCircle className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
                  {`Remove ${selectedStepIds.length}`}
                </button>
              )}
            </div>
          </div>
        )}

        {visibleGroups.length ? visibleGroups.map((group) => {
          const showAll = expandedGroupKeys.has(group.key);
          const rows = showAll ? group.matchedEntries : group.matchedEntries.slice(0, GROUP_ROW_LIMIT);
          const hiddenCount = group.matchedEntries.length - rows.length;
          const groupIds = group.allEntries.map((entry) => entry.id);
          const groupSelected = groupIds.length > 0 && groupIds.every((id) => selectedSet.has(id));
          return (
            <div key={group.key}>
              <div className={`grid ${STEP_GRID} border-b border-slate-100 bg-slate-50/50 px-3 py-2 text-[11px] font-semibold uppercase tracking-[0.1em] text-slate-400`}>
                <span>
                  {canSelect && (
                    <input
                      type="checkbox"
                      checked={groupSelected}
                      onChange={() => onToggleAllSteps(groupIds)}
                      disabled={!canRemoveSteps}
                      className="h-4 w-4 rounded border-slate-300 text-teal-600 focus:ring-teal-500 disabled:cursor-not-allowed disabled:opacity-60"
                      aria-label={`Select every step on page ${group.pageNumber}`}
                    />
                  )}
                </span>
                <span>#</span>
                <span className="hidden sm:block">Action</span>
                <span>Target</span>
                <span className="hidden sm:block">Data</span>
                <span>Confidence</span>
              </div>
              <div className="divide-y divide-slate-100">
                {rows.map((entry) => (
                  <StepRow
                    key={entry.id}
                    entry={entry}
                    isSelected={selectedSet.has(entry.id)}
                    isActive={activeStepId === entry.id}
                    canSelect={canSelect}
                    canRemoveSteps={canRemoveSteps}
                    onToggleStep={onToggleStep}
                    onOpenStep={openStep}
                    binding={stepValueBinding(entry.step, variableNames)}
                    bindingEditable={
                      canEditBindings
                      && CONSUMABLE_STEP_ACTIONS.has(String(entry.step.action || '').toLowerCase())
                    }
                    bindingVariables={variablesAvailableAt(entry.index)}
                    isSavingBinding={isSavingBinding}
                    onUseJourneyVariable={onUseJourneyVariable}
                    tabLabel={
                      showStepTabs && entry.step.tab_key
                        ? tabLabelsByKey.get(String(entry.step.tab_key)) || entry.step.tab_key
                        : ''
                    }
                  />
                ))}
                {!rows.length && (
                  <p className="px-4 py-6 text-center text-sm text-slate-500">
                    Only the page navigation matches the current filters.
                  </p>
                )}
                {hiddenCount > 0 && (
                  <button
                    type="button"
                    onClick={() => setExpandedGroupKeys((current) => new Set(current).add(group.key))}
                    className="workspace-accent-text w-full px-3 py-2 text-xs font-semibold hover:underline"
                  >
                    {`Show remaining ${hiddenCount} step(s)`}
                  </button>
                )}
              </div>
            </div>
          );
        }) : (
          <div className="p-8 text-center">
            <p className="text-sm font-semibold text-slate-900">No steps match on this page</p>
            <button type="button" onClick={clearFilters} className="workspace-accent-text mt-1 text-xs font-semibold">
              Clear search and filters
            </button>
          </div>
        )}

        <footer className="flex items-center justify-between gap-3 border-t border-slate-200 bg-slate-50/60 px-3 py-2.5">
          <button
            type="button"
            onClick={() => movePage(-1)}
            disabled={activePageIndex === 0}
            className="secondary-btn px-3 py-1.5 text-xs disabled:cursor-not-allowed disabled:opacity-40"
          >
            <ChevronLeft className="h-3.5 w-3.5" />
            Previous
          </button>
          <span className="text-xs font-semibold text-slate-500">{`${activePageIndex + 1} / ${groups.length}`}</span>
          <button
            type="button"
            onClick={() => movePage(1)}
            disabled={activePageIndex >= groups.length - 1}
            className="secondary-btn px-3 py-1.5 text-xs disabled:cursor-not-allowed disabled:opacity-40"
          >
            Next
            <ChevronRight className="h-3.5 w-3.5" />
          </button>
        </footer>
      </section>
    </div>
  );
};

export const CapturedStepsPanel = React.memo(CapturedStepsPanelBase);

/* -------------------------------------------------------------- step drawer --- */

const DetailRow = ({ label, children }) => (
  <div className="grid grid-cols-[110px_minmax(0,1fr)] items-start gap-3 py-2">
    <span className="text-xs font-semibold text-slate-500">{label}</span>
    <span className="min-w-0 text-sm text-slate-800">{children}</span>
  </div>
);

const CopyableBlock = ({ label, value, mono = true }) => {
  const [copied, setCopied] = useState(false);
  if (!value) return null;
  const copy = async () => {
    try {
      await navigator.clipboard?.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      setCopied(false);
    }
  };
  return (
    <div>
      <p className="text-xs font-semibold text-slate-500">{label}</p>
      <div className="mt-1 flex items-start gap-2 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2">
        <code className={`min-w-0 flex-1 break-all text-xs text-slate-700 ${mono ? 'font-mono' : ''}`}>{value}</code>
        <button
          type="button"
          onClick={copy}
          aria-label={`Copy ${label}`}
          className="flex-shrink-0 rounded-lg p-1 text-slate-400 transition-colors hover:bg-white hover:text-slate-700"
        >
          {copied ? <CheckCircle2 className="h-4 w-4 text-emerald-600" /> : <Copy className="h-4 w-4" />}
        </button>
      </div>
    </div>
  );
};

const StepEvidence = ({ sessionId, screenshot }) => {
  const [state, setState] = useState({ status: 'idle', url: '' });

  useEffect(() => {
    const screenshotId = String(screenshot?.id || '');
    if (!sessionId || !screenshotId) {
      setState({ status: 'empty', url: '' });
      return undefined;
    }
    let objectUrl = '';
    let cancelled = false;
    setState({ status: 'loading', url: '' });
    apiFetch(`${API_BASE_URL}${API_ENDPOINTS.FEATURE_CAPTURE_SCREENSHOT(sessionId, screenshotId)}`)
      .then((response) => (response.ok ? response.blob() : Promise.reject(new Error('unavailable'))))
      .then((blob) => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        setState({ status: 'ready', url: objectUrl });
      })
      .catch(() => {
        if (!cancelled) setState({ status: 'error', url: '' });
      });
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [sessionId, screenshot?.id]);

  if (state.status === 'loading') {
    return (
      <p className="flex items-center gap-2 py-6 text-sm text-slate-500">
        <LoaderCircle className="h-4 w-4 animate-spin" />
        Loading screenshot...
      </p>
    );
  }
  if (state.status === 'ready') {
    return (
      <figure className="space-y-2">
        <img
          src={state.url}
          alt={`Screenshot captured at ${screenshot?.title || 'this step'}`}
          className="w-full rounded-xl border border-slate-200"
        />
        {screenshot?.title && (
          <figcaption className="text-xs text-slate-500">{screenshot.title}</figcaption>
        )}
      </figure>
    );
  }
  return (
    <div className="py-8 text-center">
      <ImageOff className="mx-auto h-7 w-7 text-slate-300" />
      <p className="mt-2 text-sm text-slate-500">
        {state.status === 'error' ? 'That screenshot could not be loaded.' : 'No screenshot was captured for this step.'}
      </p>
    </div>
  );
};

const DRAWER_TABS = ['Details', 'Locator', 'Data', 'Evidence'];
const EDITABLE_STEP_ACTIONS = ['click', 'fill', 'select', 'check', 'uncheck', 'submit', 'assert'];

/**
 * The full record of one step, opened beside the list instead of pushing the table wider.
 * Locator, captured data, and evidence each get room without crowding the flow view.
 */
export const StepDetailDrawer = ({
  entry,
  totalSteps,
  pageLabel = '',
  sessionId = '',
  onClose,
  onPrevious,
  onNext,
  hasPrevious = false,
  hasNext = false,
  onSave,
  canEdit = false,
  isSaving = false,
  onDelete,
  canDelete = false,
  isDeleting = false,
  binding = null,
  bindingEditable = false,
  isSavingBinding = false,
  bindingVariables = [],
  onUseJourneyVariable,
}) => {
  const [tab, setTab] = useState('Details');
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState({ action: '', target: '', pageName: '', url: '', locator: '' });
  const [draftError, setDraftError] = useState('');

  useEffect(() => {
    setTab('Details');
    setIsEditing(false);
    setDraftError('');
  }, [entry?.id]);
  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key === 'Escape') onClose?.();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  if (!entry) return null;
  const { step, index, id: stepId } = entry;
  const quality = qualityForStep(step);
  const Icon = ACTION_ICONS[String(step.action || '').toLowerCase()] || MousePointerClick;
  const actionCanChange = EDITABLE_STEP_ACTIONS.includes(String(step.action || '').toLowerCase());

  const beginEditing = () => {
    setDraft({
      action: String(step.action || ''),
      target: String(step.target || step.label || ''),
      pageName: String(pageLabel || ''),
      url: String(step.url || ''),
      locator: String(step.locator || ''),
    });
    setDraftError('');
    setTab('Details');
    setIsEditing(true);
  };

  const cancelEditing = () => {
    setIsEditing(false);
    setDraftError('');
  };

  const saveEditing = async () => {
    const target = draft.target.trim();
    const pageName = draft.pageName.trim();
    const url = draft.url.trim();
    if (!target) {
      setDraftError('Enter a target name.');
      return;
    }
    if (!pageName) {
      setDraftError('Enter a page name.');
      return;
    }
    if (url) {
      try {
        const parsedUrl = new URL(urlForValidation(url));
        if (!['http:', 'https:'].includes(parsedUrl.protocol)) throw new Error('unsupported protocol');
      } catch {
        setDraftError('Enter a valid HTTP or HTTPS page URL. Dynamic values can use {{binding_name}}.');
        return;
      }
    }
    const updates = {};
    if (target !== String(step.target || step.label || '').trim()) updates.target = target;
    if (draft.locator.trim() !== String(step.locator || '').trim()) updates.locator = draft.locator.trim();
    if (pageName !== String(pageLabel || '').trim()) updates.page_name = pageName;
    if (url !== String(step.url || '').trim()) updates.url = url;
    if (actionCanChange && draft.action !== String(step.action || '')) updates.action = draft.action;
    if (!Object.keys(updates).length) {
      cancelEditing();
      return;
    }
    const saved = await onSave?.(stepId, updates);
    if (!saved) return;
    setIsEditing(false);
    setDraftError('');
  };

  return createPortal(
    <aside
      role="dialog"
      aria-label={`Captured step ${index + 1} details`}
      className="fixed bottom-0 right-0 top-16 z-[60] flex w-full max-w-[480px] flex-col border-l border-slate-200 bg-white shadow-2xl md:top-[76px] md:rounded-tl-2xl"
    >
      <header className="flex shrink-0 items-center justify-between gap-3 border-b border-slate-200 bg-slate-50/80 px-4 py-3">
        <div className="min-w-0">
          <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-teal-700">Captured step</p>
          <h3 className="mt-0.5 text-sm font-bold text-slate-900">{`Step ${index + 1} of ${totalSteps}`}</h3>
        </div>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={onPrevious}
            disabled={!hasPrevious}
            aria-label="Previous step"
            className="rounded-lg border border-slate-200 p-1.5 text-slate-600 transition-colors hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={onNext}
            disabled={!hasNext}
            aria-label="Next step"
            className="rounded-lg border border-slate-200 p-1.5 text-slate-600 transition-colors hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
          {!isEditing && canEdit && (
            <button
              type="button"
              onClick={beginEditing}
              aria-label="Edit captured step"
              className="ml-1 inline-flex items-center gap-1.5 rounded-lg border border-teal-200 bg-teal-50 px-2.5 py-1.5 text-xs font-semibold text-teal-700 transition-colors hover:bg-teal-100"
            >
              <Pencil className="h-3.5 w-3.5" />
              Edit
            </button>
          )}
          {isEditing && (
            <>
              <button
                type="button"
                onClick={cancelEditing}
                disabled={isSaving}
                className="ml-1 rounded-lg px-2.5 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-100 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={saveEditing}
                disabled={isSaving}
                className="inline-flex items-center gap-1.5 rounded-lg bg-teal-700 px-2.5 py-1.5 text-xs font-semibold text-white hover:bg-teal-800 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {isSaving ? <LoaderCircle className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
                Save
              </button>
            </>
          )}
          <button
            type="button"
            onClick={onClose}
            aria-label="Close step details"
            title="Close"
            className="ml-1 rounded-lg border border-slate-200 bg-white p-1.5 text-slate-500 shadow-sm transition-colors hover:bg-slate-100 hover:text-slate-800"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </header>

      <div className="px-4 pt-4">
        <div className="workspace-accent-soft-panel flex items-center gap-3 rounded-xl border px-3 py-3">
          <span className="inline-flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-xl bg-white text-slate-600">
            <Icon className="h-4 w-4" aria-hidden="true" />
          </span>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-bold text-slate-900">{normalizeAction(step.action)}</p>
            <p className="truncate text-xs text-slate-600" title={step.target}>
              {step.target || step.label || 'Target'}
            </p>
          </div>
          <span className={`flex-shrink-0 rounded-full px-2 py-1 text-[11px] font-semibold ${quality.className}`}>
            {quality.label}
          </span>
        </div>
      </div>

      {!isEditing && (
        <div className="mt-3 flex shrink-0 items-center gap-5 overflow-x-auto border-b border-slate-200 px-4">
          {DRAWER_TABS.map((name) => (
            <button
              key={name}
              type="button"
              onClick={() => setTab(name)}
              aria-pressed={tab === name}
              className={`-mb-px border-b-2 pb-2.5 text-sm font-semibold transition-colors ${
                tab === name
                  ? 'workspace-accent-text workspace-accent-border-bottom'
                  : 'border-transparent text-slate-500 hover:text-slate-900'
              }`}
            >
              {name}
            </button>
          ))}
        </div>
      )}

      <div className="min-h-0 flex-1 overflow-y-auto px-4 py-3">
        {isEditing && (
          <div className="space-y-4">
            <div className="flex items-start justify-between gap-3 border-b border-slate-200 pb-3">
              <div>
                <h4 className="text-sm font-bold text-slate-900">Edit step</h4>
                <p className="mt-1 text-xs text-slate-500">Correct the interaction details used for generation.</p>
              </div>
              <span className="rounded-full bg-amber-50 px-2 py-1 text-[10px] font-bold text-amber-700">Draft</span>
            </div>

            <label className="block">
              <span className="text-xs font-semibold text-slate-600">Action</span>
              {actionCanChange ? (
                <select
                  value={draft.action}
                  onChange={(event) => setDraft((current) => ({ ...current, action: event.target.value }))}
                  className="mt-1.5 w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-800 focus:border-teal-500 focus:outline-none focus:ring-2 focus:ring-teal-100"
                >
                  {EDITABLE_STEP_ACTIONS.map((action) => (
                    <option key={action} value={action}>{normalizeAction(action)}</option>
                  ))}
                </select>
              ) : (
                <span className="mt-1.5 block rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm text-slate-600">
                  {normalizeAction(step.action)}
                </span>
              )}
            </label>

            <label className="block">
              <span className="text-xs font-semibold text-slate-600">Target name</span>
              <input
                value={draft.target}
                onChange={(event) => {
                  setDraft((current) => ({ ...current, target: event.target.value }));
                  setDraftError('');
                }}
                className="mt-1.5 w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-800 focus:border-teal-500 focus:outline-none focus:ring-2 focus:ring-teal-100"
              />
            </label>

            <div className="space-y-4">
              <label className="block">
                <span className="text-xs font-semibold text-slate-600">Page name</span>
                <input
                  value={draft.pageName}
                  onChange={(event) => {
                    setDraft((current) => ({ ...current, pageName: event.target.value }));
                    setDraftError('');
                  }}
                  className="mt-1.5 w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-800 focus:border-teal-500 focus:outline-none focus:ring-2 focus:ring-teal-100"
                />
              </label>

              <label className="block">
                <span className="text-xs font-semibold text-slate-600">Page URL</span>
                <input
                  type="text"
                  value={draft.url}
                  onChange={(event) => {
                    setDraft((current) => ({ ...current, url: event.target.value }));
                    setDraftError('');
                  }}
                  placeholder="https://example.com/page?id={{account_id}}"
                  className="mt-1.5 w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-800 focus:border-teal-500 focus:outline-none focus:ring-2 focus:ring-teal-100"
                />
              </label>
            </div>

            <label className="block">
              <span className="text-xs font-semibold text-slate-600">Locator</span>
              <textarea
                value={draft.locator}
                onChange={(event) => setDraft((current) => ({ ...current, locator: event.target.value }))}
                rows={5}
                placeholder="No locator"
                className="mt-1.5 w-full resize-y rounded-lg border border-slate-300 bg-white px-3 py-2.5 font-mono text-xs leading-5 text-slate-800 focus:border-teal-500 focus:outline-none focus:ring-2 focus:ring-teal-100"
              />
              <span className="mt-1.5 block text-xs leading-5 text-slate-500">
                A changed locator is marked for verification until it is checked against the live page.
              </span>
            </label>

            {draftError && (
              <p className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-xs font-semibold text-rose-700">
                {draftError}
              </p>
            )}
          </div>
        )}

        {!isEditing && tab === 'Details' && (
          <div className="divide-y divide-slate-100">
            <DetailRow label="Action">{normalizeAction(step.action)}</DetailRow>
            <DetailRow label="Target">{step.target || step.label || '—'}</DetailRow>
            <DetailRow label="Page">{pageLabel || '—'}</DetailRow>
            <DetailRow label="Locator confidence">
              <span className={`rounded-full px-2 py-1 text-[11px] font-semibold ${quality.className}`}>
                {quality.label}
              </span>
            </DetailRow>
            {verificationSummary(step) && (
              <DetailRow label="Verification">{verificationSummary(step)}</DetailRow>
            )}
            {step.url && <DetailRow label="URL"><span className="break-all text-xs">{step.url}</span></DetailRow>}
          </div>
        )}

        {!isEditing && tab === 'Locator' && (
          <div className="space-y-3">
            <CopyableBlock label="Locator" value={step.locator || ''} />
            {!step.locator && (
              <p className="text-sm text-slate-500">
                This step has no locator — it is a page navigation.
              </p>
            )}
            <div className="divide-y divide-slate-100">
              {step.locator_strategy && <DetailRow label="Strategy">{step.locator_strategy}</DetailRow>}
              <DetailRow label="Score">
                {step.action === 'navigate' ? 'Navigation step' : (step.locator_score ?? 'Not scored')}
              </DetailRow>
              {step.locator_match_count !== undefined && step.locator_match_count !== null && (
                <DetailRow label="Matches">{`${step.locator_match_count} element(s)`}</DetailRow>
              )}
              <DetailRow label="Verified">
                {step.locator_identity_verified ? 'Resolved against the live DOM' : 'Not verified'}
              </DetailRow>
            </div>
          </div>
        )}

        {!isEditing && tab === 'Data' && (
          <div className="space-y-3">
            <div>
              <p className="text-xs font-semibold text-slate-500">Runtime value</p>
              <div className="mt-1">
                <StepValueBinding
                  binding={binding}
                  canEdit={bindingEditable}
                  isSaving={isSavingBinding}
                  variables={bindingVariables}
                  onUse={(variableId) => onUseJourneyVariable?.(stepId, variableId)}
                />
              </div>
            </div>
            {step.masked ? (
              <p className="text-sm text-slate-500">The captured value is masked.</p>
            ) : (
              <CopyableBlock label="Captured value" value={step.value || ''} mono={false} />
            )}
            {!step.value && !step.masked && (
              <p className="text-sm text-slate-500">No value was recorded for this step.</p>
            )}
          </div>
        )}

        {!isEditing && tab === 'Evidence' && <StepEvidence sessionId={sessionId} screenshot={step.screenshot} />}
      </div>

      {canDelete && (
        <footer className="flex shrink-0 justify-end border-t border-slate-200 bg-slate-50/60 px-4 py-3">
          <button
            type="button"
            onClick={() => onDelete?.(stepId)}
            disabled={isDeleting}
            className="inline-flex items-center justify-center gap-2 rounded-lg border border-rose-200 bg-white px-3 py-2 text-sm font-semibold text-rose-700 transition-colors hover:bg-rose-50 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {isDeleting ? <LoaderCircle className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
            Delete step
          </button>
        </footer>
      )}
    </aside>,
    document.body
  );
};

/* ------------------------------------------------------------- runtime data --- */

const VARIABLE_STATUS_LABELS = {
  no_producer: 'Missing capture step',
  missing_producer: 'Missing capture step',
  unused: 'Never used',
  missing_consumer: 'Never used',
  duplicate_name: 'Duplicate name',
  invalid_name: 'Invalid name',
};

const variableStatus = (issues) => {
  if (!issues.length) return { tone: 'ok', label: 'Ready', title: 'This value is read and used.' };
  const blocking = issues.find((issue) => issue?.severity === 'error') || issues[0];
  const label = VARIABLE_STATUS_LABELS[String(blocking?.code || '')]
    || String(blocking?.message || 'Needs attention').slice(0, 40);
  return {
    tone: blocking?.severity === 'error' ? 'error' : 'warning',
    label,
    title: issues.map((issue) => issue.message).join('\n'),
  };
};

const JourneyVariablesPanelBase = ({
  variables = [],
  validation = {},
  steps = [],
  captureTabs = [],
  onRename,
  onDelete,
  onChangeRowRule,
  canEdit = true,
}) => {
  const [query, setQuery] = useState('');

  const issues = useMemo(
    () => (Array.isArray(validation?.issues) ? validation.issues : []),
    [validation]
  );

  const rows = useMemo(() => {
    const tabLabelsByKey = new Map(
      captureTabs.map((tab) => [String(tab.tab_key || ''), String(tab.label || tab.origin || tab.tab_key || '')]),
    );
    const stepById = new Map(steps.map((step) => [String(step?.id || ''), step]));
    const orderByStepId = new Map(steps.map((step, index) => [String(step?.id || ''), index + 1]));
    const consumersByVariableId = new Map();
    steps.forEach((step) => {
      const variableId = String(step?.value_source?.variable_id || '');
      if (step?.value_source?.kind !== 'variable' || !variableId) return;
      consumersByVariableId.set(variableId, (consumersByVariableId.get(variableId) || 0) + 1);
    });

    return variables.map((variable) => {
      const variableIssues = issues.filter((issue) => issue?.variable_id === variable.id);
      const producerStepId = String(variable?.producer?.step_id || '');
      const producerStep = stepById.get(producerStepId);
      const producerTabKey = String(
        variable?.producer?.tab_key || variable?.source?.tab_key || producerStep?.tab_key || ''
      );
      const producerTabLabel = producerTabKey
        ? tabLabelsByKey.get(producerTabKey) || producerTabKey
        : '';
      return {
        variable,
        issues: variableIssues,
        status: variableStatus(variableIssues),
        sourceLabel: variable?.source?.kind === 'table_cell' ? 'table' : 'page',
        producerOrder: orderByStepId.get(producerStepId),
        producerTabLabel,
        consumers: consumersByVariableId.get(String(variable.id)) || 0,
      };
    });
  }, [variables, issues, steps, captureTabs]);

  const attentionCount = rows.filter((row) =>
    row.issues.some((issue) => issue?.severity === 'error')
  ).length;
  const needle = query.trim().toLowerCase();
  const visible = needle
    ? rows.filter((row) => String(row.variable.name || '').toLowerCase().includes(needle))
    : rows;

  if (!variables.length) {
    return (
      <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-8 text-center">
        <Variable className="mx-auto h-8 w-8 text-slate-400" />
        <h4 className="mt-3 text-sm font-bold text-slate-900">No runtime values captured</h4>
        <p className="mx-auto mt-1 max-w-xl text-sm leading-6 text-slate-500">
          Values saved during capture appear here so generated scripts read them from the page
          instead of hard-coding what you typed.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {attentionCount > 0 && (
        <div className="flex items-start gap-3 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3">
          <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0 text-rose-600" aria-hidden="true" />
          <div className="min-w-0">
            <p className="text-sm font-bold text-rose-800">
              {`${formatCountLabel(attentionCount, 'variable')} need attention`}
            </p>
            <p className="mt-0.5 text-xs leading-5 text-rose-700">
              These values are read during the test run but do not have a captured step.
              Recapture the value or delete the variable.
            </p>
          </div>
        </div>
      )}

      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-xs text-slate-500">
          The value seen during capture is only evidence. Generated scripts read these from the
          page at run time, so they survive changing data.
        </p>
        <div className="flex flex-wrap items-center gap-2">
          <label className="relative flex items-center">
            <Search className="pointer-events-none absolute left-3 h-3.5 w-3.5 text-slate-400" aria-hidden="true" />
            <input
              type="search"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search variables..."
              aria-label="Search runtime variables"
              className="w-[200px] rounded-xl border border-slate-200 bg-white py-2 pl-8 pr-3 text-xs text-slate-700 placeholder:text-slate-400 focus:border-slate-300 focus:outline-none"
            />
          </label>
        </div>
      </div>

      <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white shadow-sm">
        <div className="min-w-[720px]">
          <div className="grid grid-cols-[minmax(0,1.2fr)_90px_100px_minmax(0,1fr)_190px] border-b border-slate-200 bg-slate-50/80 px-4 py-2.5 text-[11px] font-semibold uppercase tracking-[0.1em] text-slate-400">
            <span>Variable</span>
            <span>Source</span>
            <span>Usage</span>
            <span>Status</span>
            <span className="text-right">Actions</span>
          </div>
          <div className="divide-y divide-slate-100">
            {visible.map((row) => (
              <div
                key={row.variable.id}
                className="grid grid-cols-[minmax(0,1.2fr)_90px_100px_minmax(0,1fr)_190px] items-center px-4 py-2.5"
              >
                <span className="min-w-0">
                  <span className="block truncate font-mono text-[13px] font-bold text-slate-800" title={row.variable.name}>
                    {row.variable.name}
                  </span>
                  {row.producerTabLabel && (
                    <span className="text-[11px] text-slate-500">{`Read in ${row.producerTabLabel}`}</span>
                  )}
                </span>
                <span className="text-xs text-slate-600">{row.sourceLabel}</span>
                <span className="text-xs text-slate-600">{formatCountLabel(row.consumers, 'step')}</span>
                <span
                  className={`inline-flex min-w-0 items-center gap-1.5 text-xs font-semibold ${
                    row.status.tone === 'error'
                      ? 'text-rose-700'
                      : row.status.tone === 'warning'
                        ? 'text-amber-700'
                        : 'text-emerald-700'
                  }`}
                  title={row.status.title}
                >
                  <span
                    className={`h-1.5 w-1.5 flex-shrink-0 rounded-full ${
                      row.status.tone === 'error'
                        ? 'bg-rose-500'
                        : row.status.tone === 'warning'
                          ? 'bg-amber-500'
                          : 'bg-emerald-500'
                    }`}
                    aria-hidden="true"
                  />
                  <span className="truncate">{row.status.label}</span>
                </span>
                <span className="flex items-center justify-end gap-1.5">
                  {canEdit && row.variable?.source?.kind === 'table_cell' && (
                    <select
                      value={
                        row.variable?.source?.row_rule?.type === 'captured'
                          ? 'captured'
                          : row.variable?.source?.row_rule?.type || 'first'
                      }
                      onChange={(event) => onChangeRowRule?.(row.variable, event.target.value)}
                      className="rounded-lg border border-slate-200 bg-white px-2 py-1 text-[11px] font-semibold text-slate-700"
                      title="Which row of the table this value is read from"
                    >
                      <option value="first">First row</option>
                      <option value="last">Last row</option>
                      <option value="captured">
                        {`Row ${Number(row.variable?.source?.row_rule?.index || 0) + 1}`}
                      </option>
                    </select>
                  )}
                  {canEdit && (
                    <>
                      <button
                        type="button"
                        onClick={() => onRename?.(row.variable)}
                        className="rounded-lg border border-slate-200 bg-white px-2.5 py-1 text-[11px] font-semibold text-slate-700 hover:bg-slate-50"
                      >
                        Rename
                      </button>
                      <button
                        type="button"
                        onClick={() => onDelete?.(row.variable)}
                        className="rounded-lg border border-rose-200 bg-white px-2.5 py-1 text-[11px] font-semibold text-rose-700 hover:bg-rose-50"
                      >
                        Delete
                      </button>
                    </>
                  )}
                </span>
              </div>
            ))}
            {!visible.length && (
              <p className="px-4 py-6 text-center text-sm text-slate-500">No variables match that search.</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export const JourneyVariablesPanel = React.memo(JourneyVariablesPanelBase);
